import React, { useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { loginRequest, isAzureADEnabled } from '../config/authConfig';

interface AzureLoginButtonProps {
  onLoginSuccess?: (accessToken: string, id_token?: string) => void;
  onLoginError?: (error: any) => void;
  className?: string;
  useRedirect?: boolean;
}

const MicrosoftLogo: React.FC<{ className?: string }> = ({ className = "w-5 h-5" }) => (
  <svg
    className={className}
    viewBox="0 0 21 21"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect x="1" y="1" width="9" height="9" fill="#F25022" />
    <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
    <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
    <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
  </svg>
);

const AzureLoginButton: React.FC<AzureLoginButtonProps> = ({
  onLoginSuccess,
  onLoginError,
  className = '',
  useRedirect = false
}) => {
  const { instance } = useMsal();
  const [loading, setLoading] = useState(false);

  // If Azure AD is not enabled, show disabled button
  if (!isAzureADEnabled) {
    return (
      <button
        type="button"
        disabled
        className={className || `
          w-full flex justify-center items-center py-2 px-4 border border-gray-300 rounded-md shadow-sm
          text-sm font-medium text-gray-400 bg-gray-100 cursor-not-allowed
        `.trim()}
      >
        <MicrosoftLogo className="w-5 h-5 mr-2" />
        Microsoft Sign-in Disabled
      </button>
    );
  }

  const handleLogin = async () => {
    setLoading(true);
    
    try {
      if (useRedirect) {
        // Login with redirect
        await instance.loginRedirect(loginRequest);
      } else {
        // Login with popup
        const response = await instance.loginPopup(loginRequest);
        
        if (response && response.accessToken) {
          // id_token contains groups claim for internal domain validation
          // MSAL exposes it as idToken (camelCase); also check id_token for compatibility
          const raw = (response as any).id_token ?? (response as any).idToken;
          const id_token = typeof raw === 'string' && raw.trim() ? raw.trim() : undefined;
          onLoginSuccess?.(response.accessToken, id_token);
        }
      }
    } catch (error: any) {
      console.error('Azure AD login failed:', error);
      onLoginError?.(error);
    } finally {
      setLoading(false);
    }
  };

  const defaultClassName = `
    w-full flex justify-center items-center py-2 px-4 border border-gray-300 rounded-md shadow-sm
    text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2
    focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed
    transition-colors duration-200
  `.trim();

  return (
    <button
      type="button"
      onClick={handleLogin}
      disabled={loading}
      className={className || defaultClassName}
    >
      <MicrosoftLogo className="w-5 h-5 mr-2" />
      {loading ? 'Signing in...' : 'Sign in with Microsoft'}
    </button>
  );
};

export default AzureLoginButton;